<template>
  <Modal v-model="isVisible" width="md">
    <div slot="header">
      Upload de arquivo CSV
    </div>

    <!-- File Upload Section -->
    <template v-if="!selectedFile">
      <p class="my-2">Baixe o arquivo modelo, preencha com as informações da jornada dos motoristas e faça o upload
        aqui.</p>
      <Upload :isMultiple="false" accept=".csv, .CSV" @changeFiles="handleFileUpload">
        <h5>Clique ou arraste o arquivo CSV preenchido para carregar os veículos em lote</h5>
      </Upload>
    </template>

    <p class="my-2" v-else>Arquivo selecionado: <strong>{{ selectedFile.name }}</strong></p>

    <!-- Error Section -->
    <div v-if="uploadErrors.length > 0" class="my-3">
      <hr />
      <div class="error-header">
        <XIcon size="1.5x" class="mr-2" />
        <strong>Erro ao processar a planilha.</strong>
      </div>
      <div class="error-subtitle">
        Os erros encontrados estão listados abaixo:
      </div>
      <div class="error-list">
        <div v-for="(error, index) in uploadErrors" :key="index" class="error-item">
          {{ error }}
        </div>
      </div>
    </div>

    <!-- Instructions Section -->
    <div v-if="showInstructions" class="my-2 mx-2">
      <hr />
      <div class="instructions-content">
        <h4>📘 Instruções</h4>

        <h5>✅ Como utilizar a funcionalidade:</h5>
        <ol>
          <li>Clique no botão "Cruzamento" para abrir o modal de upload;</li>
          <li>Baixe o arquivo modelo clicando em "Baixar arquivo modelo";</li>
          <li>Preencha o arquivo CSV com as informações da escala de trabalho dos motoristas:</li>
        </ol>

        <h5>📋 Preenchimento dos dados:</h5>
        <ul>
          <li><strong>Data inicial (Coluna A)</strong>: Data/hora em que o motorista assumiu o veículo.</li>
          <li><strong>Data final (Coluna B)</strong>: Data/hora em que o motorista devolveu o veículo (Campo
            obrigatório)
          </li>
          <li><strong>CPF (Coluna C)</strong>: CPF do motorista (Campo obrigatório)</li>
          <li><strong>Placa (Coluna D)</strong>: Placa do veículo</li>
          <li><strong>Renavam (Coluna E)</strong>: Renavam do veículo (Campo obrigatório)</li>
          <li><strong>Nome do motorista (Coluna F)</strong>: Nome do condutor do veículo</li>
          <li><strong>Categoria do motorista (Coluna G)</strong>: Categoria do condutor do veículo. Exemplo: frotista,
            autônomo...</li>
        </ul>

        <h5>⚠️ Regras importantes:</h5>
        <p><strong>📅 Datas:</strong></p>
        <ul>
          <li>Aceita formato ISO (2024-07-22 14:30:00) ou europeu (22/07/2024 14:30:00)</li>
          <li>Pode-se inserir apenas a data (22/07/2024) ou a data com hora (22/07/2024 14:30:00)</li>
        </ul>

        <p><strong>⚠️ ATENÇÃO:</strong></p>
        <ul>
          <li>Não insira a mesma data nos campos inicial e final (ex: 22/07/2024 em ambos), pois o sistema entenderá
            como
            período da meia-noite à meia-noite</li>
          <li>Caso a Categoria do motorista ou o Nome do motorista sejam informados incorretamente, é possível
            sobrescrever
            a informação enviando um novo arquivo, pois o sistema identifica o motorista pelo CPF</li>
        </ul>

        <p><strong>🚗 Veículo:</strong></p>
        <ul>
          <li>É necessário preencher ao menos Placa ou Renavam (ou ambos)</li>
          <li>Essas colunas não podem ficar vazias simultaneamente</li>
        </ul>
      </div>
      <hr />
    </div>

    <!-- Footer Section -->
    <div slot="footer">
      <!-- Buttons when showing errors -->
      <template v-if="uploadErrors.length > 0">
        <button class="btn btn-outline mt-1 mr-1" @click="clearErrors">
          Limpar erros
        </button>
        <button class="btn btn-primary mt-1 mr-1" @click="downloadErrors">
          Baixar erros
        </button>
      </template>

      <!-- Buttons when file is selected (and no errors) -->
      <template v-else-if="selectedFile">
        <button class="btn btn-primary mt-1 mr-1" :disabled="accountIsClosed" @click="uploadScheduleCrossing">
          Enviar
        </button>
        <button class="btn btn-outline mt-1 mr-1" @click="cancelUpload">
          Cancelar
        </button>
      </template>

      <!-- Default buttons when no file selected -->
      <template v-else>
        <button class="btn btn-outline mt-1 mr-1" @click="showInstructions = !showInstructions">
          Instruções
        </button>
        <button class="btn btn-primary mt-1 mr-1" @click="downloadTemplate">
          Baixar arquivo modelo
        </button>
      </template>
    </div>
  </Modal>
</template>

<script>
import Modal from '../shares/specialTag/Modal'
import Upload from '../shares/specialTag/Upload'
import csvToMatrix from '@/models/csvToMatrix'
import compressMatrix from '@/models/compressMatrix'

import GetAccount from '@/mixins/GetAccount'

import { XIcon } from 'vue-feather-icons'

export default {
  name: 'ScheduleCrossingModal',
  components: {
    Modal,
    Upload,
    XIcon
  },
  mixins: [GetAccount],
  props: {
    value: {
      type: Boolean,
      default: false
    }
  },
  data: () => ({
    selectedFile: null,
    showInstructions: false,
    uploadErrors: []

  }),
  computed: {
    isVisible: {
      get() {
        return this.value
      },
      set(value) {
        this.$emit('input', value)
      }
    }
  },
  methods: {
    downloadTemplate() {
      const csvContent = '"Data inicial";"Data final";"CPF";"Placa";"Renavam";"Nome do motorista";"Categoria do motorista"\\n'
      const blob = new Blob([csvContent], { type: 'text/csv' })
      const url = window.URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.download = 'template_jornada_motorista.csv'
      link.click()
      window.URL.revokeObjectURL(url)
      link.remove()
    },
    handleFileUpload(files) {
      this.selectedFile = files[0]
      this.showInstructions = false
      this.uploadErrors = []
    },
    cancelUpload() {
      this.isVisible = false
      this.selectedFile = null
    },
    clearErrors() {
      this.uploadErrors = []
    },
    downloadErrors() {
      if (this.uploadErrors.length === 0) return

      const errorContent = this.uploadErrors.join('\n')
      const timestamp = new Date().toISOString().slice(0, 19).replace(/:/g, '-')
      const blob = new Blob([errorContent], { type: 'text/plain;charset=utf-8' })
      const url = window.URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.download = `erros_cruzamento_${timestamp}.txt`
      link.click()
      window.URL.revokeObjectURL(url)
      link.remove()
    },
    async uploadScheduleCrossing() {
      this.$emit('start-loading')

      const arrayBuffer = await this.selectedFile.arrayBuffer()
      let text
      try {
        text = new TextDecoder('utf-8', { fatal: true }).decode(arrayBuffer)
      } catch {
        text = new TextDecoder('iso-8859-1').decode(arrayBuffer)
      }
      const csv = csvToMatrix(text)

      if (csv.length <= 1) {
        this.$emit('stop-loading')
        this.selectedFile = null
        alert('Não há dados validos para enviar')
        return
      }

      csv[0] = ['start_date', 'end_date', 'cpf', 'plate', 'renavam', 'driver_name', 'driver_kind']
      const compacted = compressMatrix(csv)
      const data = compacted.e < 0.9 ? compacted : csv

      try {
        await this.axios.post('/maestro/tickets/schedule_crossing', { data: data })
        this.$notify.add('Upload realizada com sucesso, o cruzamento será realizado em breve.')
        this.isVisible = false
        this.selectedFile = null
        this.$emit('upload-success')
      } catch (e) {
        if (Array.isArray(e.errors)) {
          this.uploadErrors = e.errors
        } else {
          this.uploadErrors = [e.message || 'Erro desconhecido ao processar o arquivo']
        }
      } finally {
        this.selectedFile = null
        this.$emit('stop-loading')
      }
    }
  },
  watch: {
    isVisible(newValue) {
      if (!newValue) {
        this.selectedFile = null
        this.showInstructions = false
        this.uploadErrors = []
      }
    }
  }
}
</script>

<style scoped>
.instructions-content,
.instructions-content h4,
.instructions-content h5,
.instructions-content p,
.instructions-content li {
  color: #000;
}

.error-header {
  display: flex;
  align-items: center;
  margin-bottom: 8px;
  color: #dc3545;
  font-weight: bold;
}

.error-subtitle {
  color: #6c757d;
  margin-bottom: 12px;
  font-size: 14px;
}

.error-list {
  max-height: 200px;
  overflow-y: auto;
  border: 1px solid #e9ecef;
  border-radius: 4px;
  background-color: white;
}

.error-item {
  padding: 8px 12px;
  border-bottom: 1px solid #e9ecef;
  color: #dc3545;
  font-size: 13px;
  line-height: 1.4;
  background-color: #fff3f4;
}

.error-item:last-child {
  border-bottom: none;
}
</style>
